import React, { useMemo, useState } from 'react'
import { pick } from 'lodash'
import { makeStyles } from '@mui/styles'

import lang from '@/lang'
import ThemeInterface from '@/theme-interface'
import { brandOutlineButton } from '../_helpers/brandButtons'
import { brandText } from '@/utils/brand-typography'
import { TextInput } from '@/modules/base/components/inputs'
import { ButtonSquare, SquareIconButton } from '@/modules/base/components/buttons'
import { VARIANTS as BUTTON_VARIANTS } from '@/modules/base/components/buttons/square/button-square'
import { SIZES, VARIANTS } from '@/modules/base/components/buttons/square-icon-button/square-icon-button'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import SVGIcon from '@/modules/base/components/svg-icon/svg-icon'
import GiftOutlineIcon from '@assets/images/icons/v2/gift-outline.svg'
import InfoOutlineIcon from '@assets/images/icons/v2/info-outline.svg'
import type { CouponDataType } from '@/modules/base/components/modal/upgrade-modal/order-overview/summary/overview-summary'
import { ShoppingCartItems } from '@/modules/moduleSubscription/types/ShoppingCart'
import validateCouponCode from '../_helpers/validateCouponCode'

const useStyles = makeStyles((theme: ThemeInterface) => ({
  link: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    fontSize: theme.brandTypography.text.regular.fontSize,
    fontWeight: theme.brandTypography.weights.medium,
    cursor: 'pointer',
    color: theme.palette.brand.main,
    // icon and label start on the left - doubled class, ButtonSquare centres its content
    '&&': {
      alignSelf: 'flex-start',
      justifyContent: 'flex-start',
    },
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
    color: theme.palette.brand.text.primary,
    fontSize: theme.brandTypography.text.regular.fontSize,
  },
  form: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 8,
    fontSize: theme.brandTypography.text.regular.fontSize,
  },
  input: {
    flex: 1,
    minWidth: 0,
    // white like the card - TextInput would grey it
    '& input': {
      background: theme.palette.background.paper,
    },
  },
  // always clickable: an empty or invalid code is named below the field instead
  submit: {
    ...brandOutlineButton(theme),
    width: 'auto',
    background: theme.palette.background.paper,
  },
  error: {
    display: 'flex',
    gap: 6,
    margin: '8px 0 0',
    color: theme.palette.brand.error,
    ...brandText(theme, 'tiny'),
    // the 16 px icon centred on the first line of the 18 px high text
    '& svg': {
      flexShrink: 0,
      marginTop: 1,
    },
  },
}))

interface CouponSectionProps {
  items: ShoppingCartItems
  period: string
  onCouponCodeChange: (coupon: CouponDataType | null) => void
}

// an applied coupon is listed with the sums of the sidebar, this section only redeems one
const CouponSection = ({ items, period, onCouponCodeChange }: CouponSectionProps) => {
  const classes = useStyles()
  const texts = lang.upgrade.wizard.sidebar
  const [isOpen, setIsOpen] = useState(false)
  const [code, setCode] = useState('')
  const [error, setError] = useState('')
  const [isValidating, setIsValidating] = useState(false)

  const subscriptionTypesInCart = useMemo(() => Object.values(items)
    .flatMap(products => Object.values(products)
      .filter(item => item.isProductItem || item.type)
      .map(item => pick(item, 'name', 'period'))), [items])

  const close = () => {
    setIsOpen(false)
    setCode('')
    setError('')
  }

  const redeem = async() => {
    if (isValidating) return
    if (!code.trim()) {
      setError(texts.couponRequired)
      return
    }
    setIsValidating(true)
    const coupon = await validateCouponCode(code.trim(), subscriptionTypesInCart, period)
    setIsValidating(false)
    if (!coupon) {
      setError(texts.couponInvalid)
      return
    }
    onCouponCodeChange(coupon)
    close()
  }

  if (!isOpen) {
    return (
      <ButtonSquare
        className={classes.link}
        variant={BUTTON_VARIANTS.link}
        dataQsId='checkout-coupon-open'
        onClick={() => setIsOpen(true)}>
        <SVGIcon svg={GiftOutlineIcon} width={24} height={24} fill='currentColor' />
        {texts.couponRedeem}
      </ButtonSquare>
    )
  }

  return (
    <div data-qs-id='checkout-coupon'>
      <div className={classes.header}>
        <span>{texts.couponLabel}</span>
        <SquareIconButton
          icon={CloseRoundedIcon}
          size={SIZES.smaller}
          variant={VARIANTS.icon}
          ariaLabel={texts.couponClose}
          dataQsId='checkout-coupon-close'
          onClick={close}
        />
      </div>
      <div className={classes.form}>
        <div className={classes.input}>
          {/* the field only turns red, the message with its icon follows below */}
          <TextInput
            name='couponCode'
            placeholder={texts.couponPlaceholder}
            value={code}
            errorMessage={error}
            hideErrorMessage
            autoComplete='off'
            dataQsId='checkout-coupon-input'
            onChange={(value) => {
              setCode(value)
              setError('')
            }}
            onEnter={redeem}
          />
        </div>
        <ButtonSquare
          className={classes.submit}
          variant={BUTTON_VARIANTS.outline}
          label={texts.couponSubmit}
          dataQsId='checkout-coupon-submit'
          onClick={redeem}
        />
      </div>
      {error && (
        <p className={classes.error} role='alert' data-qs-id='checkout-coupon-error'>
          <SVGIcon svg={InfoOutlineIcon} width={16} height={16} fill='currentColor' />
          {error}
        </p>
      )}
    </div>
  )
}

export default CouponSection
